import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

import OverviewCard from "../../components/OverviewCard";
import TicketStatusChart from "../../components/charts/TicketStatusChart";
import TicketAgeChart from "../../components/charts/TicketAgeChart";
import PriorityPieChart from "../../components/charts/PriorityPieChart";
import TicketQueueAreaChart from "../../components/charts/TicketQueueAreaChart";
import CreatedClosedChart from "../../components/charts/CreatedClosedChart";
import RecentOpenTicketsTable from "../../components/tables/RecentOpenTicketsTable";
import CreateTicketModal from "../../components/CreateTicketModal";
import SevenDayDrawer from "../../components/SevenDayDrawer";

import ResourceTable from "../../components/tables/ResourceTable";
import BerryStatCard from "../../components/statCard";
import InViewFadeUp from "../../components/animations/InViewFadeUp";

import {
  fetchTicketPriorityDistribution, fetchClients, fetchOverviewCombined,
  fetch24hrTicketCount, fetch24hrTickets, fetchFreshserviceOverview, getClientInsights,
  fetchOneView,
} from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";
import { useAuth } from "../../contexts/AuthContext";

import {
  FaTicketAlt, FaShieldAlt, FaServer, FaExclamationTriangle,
  FaArrowUp, FaCheckCircle, FaClock, FaHistory, FaSearch,
  FaChartLine, FaSpinner, FaTachometerAlt, FaChartBar, FaSyncAlt,
  FaTimes, FaLightbulb, FaHeadset,
} from "react-icons/fa";
import { ROUTES } from "../../routes";
import { FaPlus } from "react-icons/fa";
import { LayoutList, Timer, AlertCircle, CalendarDays, History } from "lucide-react";

import { computeAgeSummary } from "../../components/utils/ageSummary";
import OverviewChatbot from "../../components/OverviewChatbot";

// ── Tooltip component for truncated ticket titles ────────────────────────────
const TitleTooltip = ({ title }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0 });
  const ref                   = useRef(null);

  const handleMouseEnter = (e) => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
    setVisible(true);
  };

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setVisible(false)}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}
      >
        {title || "—"}
      </div>
      {visible && title && (
        <div
          style={{
            position: "fixed", top: pos.top, left: pos.left, zIndex: 99999,
            background: "var(--color-bg-card)", color: "var(--color-text)",
            border: "1px solid var(--color-border)", borderRadius: 8,
            padding: "8px 12px", fontSize: 13, fontWeight: 400,
            maxWidth: 360, wordBreak: "break-word",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            pointerEvents: "none", lineHeight: 1.5,
          }}
        >
          {title}
        </div>
      )}
    </>
  );
};

// ── Priority sort order ──────────────────────────────────────────────────────
const PRIORITY_ORDER = { Emergency: 0, Critical: 1, High: 2, Medium: 3, Low: 4, Normal: 5 };

/* ─────────────────── Client Insights Drawer ────────────────────────────── */
function CISeverityDot({ count }) {
  const color = count >= 5 ? "#ef4444" : count >= 3 ? "#f59e0b" : "#6b7280";
  return (
    <span style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      background: color, flexShrink: 0, marginTop: 3,
    }} />
  );
}

function CIInsightCard({ dot, title, meta, count, countLabel, tags, priorityBadge }) {
  return (
    <div style={{
      borderLeft: `3px solid ${dot >= 5 ? "#ef4444" : dot >= 3 ? "#f59e0b" : "#d1d5db"}`,
      paddingLeft: 12, paddingTop: 10, paddingBottom: 10,
      borderBottom: "1px solid var(--color-border)",
      display: "flex", gap: 10,
    }}>
      <CISeverityDot count={dot} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: "var(--color-text)", wordBreak: "break-word" }}>
            {title}
            {priorityBadge && (
              <span style={{
                marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                background: priorityBadge === "Emergency" ? "rgba(220,38,38,0.12)" : "rgba(245,158,11,0.12)",
                color: priorityBadge === "Emergency" ? "#dc2626" : "#b45309",
                border: `1px solid ${priorityBadge === "Emergency" ? "rgba(220,38,38,0.3)" : "rgba(245,158,11,0.3)"}`,
              }}>{priorityBadge}</span>
            )}
          </span>
          <span style={{
            flexShrink: 0, fontSize: 12, fontWeight: 700,
            color: dot >= 5 ? "#ef4444" : dot >= 3 ? "#f59e0b" : "var(--color-text-light)",
            whiteSpace: "nowrap",
          }}>
            {count}× {countLabel}
          </span>
        </div>
        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
          {tags.map((t, i) => (
            <span key={i} style={{ fontSize: 11, color: "var(--color-text-light)" }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function CIInsightsSection({ title, items, renderCard, emptyMsg }) {
  if (!items || items.length === 0) return (
    <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-light)" }}>{emptyMsg}</div>
  );
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--color-text-light)", marginBottom: 8 }}>{title}</div>
      {items.map((item, i) => renderCard(item, i))}
    </div>
  );
}

function ClientInsightsDrawer({ open, onClose, data, loading, clientName, error }) {
  if (!open) return null;
  const total = (data?.same_title?.length || 0)
              + (data?.same_queue?.length || 0)
              + (data?.recurring_priority?.length || 0);
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        zIndex: 1040, backdropFilter: "blur(2px)",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
        maxWidth: "95vw", background: "var(--color-bg-card)",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        zIndex: 1050, display: "flex", flexDirection: "column",
        borderLeft: "1px solid var(--color-border)",
      }}>
        <div style={{
          padding: "18px 20px", borderBottom: "1px solid var(--color-border)",
          display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        }}>
          <FaExclamationTriangle size={15} color="#f59e0b" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "var(--color-text)" }}>
              Ticket Insights{clientName ? ` — ${clientName}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 1 }}>
              Last 90 days · {total} pattern{total !== 1 ? "s" : ""} found
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--color-text-light)", padding: 4,
          }}>
            <FaTimes size={16} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-light)", padding: "20px 0" }}>
              <FaSpinner size={14} style={{ animation: "spin 1s linear infinite" }} />
              Analysing ticket patterns…
            </div>
          ) : error ? (
            <div style={{ padding: "20px 0", color: "#ef4444", fontSize: 13 }}>
              ⚠️ Failed to load insights. Please try again.
            </div>
          ) : (
            <>
              <CIInsightsSection
                title="Repeated Issues"
                items={data?.same_title}
                emptyMsg="No repeated issues found for this client."
                renderCard={(item, i) => (
                  <CIInsightCard key={i}
                    dot={item.occurrence_count}
                    title={`"${item.norm_title}"`}
                    count={item.occurrence_count}
                    countLabel="this period"
                    tags={[
                      `Queue: ${item.queue_name}`,
                      `First seen: ${item.first_seen}`,
                      `Last seen: ${item.last_seen}`,
                      `Assigned to: ${item.unique_engineers} engineer${item.unique_engineers !== 1 ? "s" : ""}`,
                    ]}
                  />
                )}
              />
              <CIInsightsSection
                title="High-Volume Queues"
                items={data?.same_queue}
                emptyMsg="No high-volume queue patterns."
                renderCard={(item, i) => (
                  <CIInsightCard key={i}
                    dot={item.occurrence_count}
                    title={item.queue_name}
                    count={item.occurrence_count}
                    countLabel="tickets"
                    tags={[
                      `${item.unique_titles} distinct issue${item.unique_titles !== 1 ? "s" : ""}`,
                      `First seen: ${item.first_seen}`,
                      `Last seen: ${item.last_seen}`,
                    ]}
                  />
                )}
              />
              <CIInsightsSection
                title="Recurring Priority Issues"
                items={data?.recurring_priority}
                emptyMsg="No recurring high-priority issues."
                renderCard={(item, i) => (
                  <CIInsightCard key={i}
                    dot={item.occurrence_count}
                    title={`"${item.norm_title}"`}
                    count={item.occurrence_count}
                    countLabel="occurrences"
                    priorityBadge={item.priority}
                    tags={[
                      `First seen: ${item.first_seen}`,
                      `Last seen: ${item.last_seen}`,
                    ]}
                  />
                )}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Shared inline filter input style ────────────────────────────────────────
const filterInputStyle = {
  width: "100%", fontSize: 11, padding: "3px 6px",
  border: "1px solid var(--color-border)", borderRadius: 6,
  background: "var(--color-bg-card)", color: "var(--color-text)", height: 26,
};

// ── Shared badge style ───────────────────────────────────────────────────────
const badgeBase = {
  background: "rgba(111,66,193,0.1)",
  border: "1px solid rgba(111,66,193,0.18)",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  gap: 7,
  userSelect: "none",
  transition: "background 0.15s, border-color 0.15s",
};

// ── Data source registry — add new integrations here ────────────────────────
const AVAILABLE_SOURCES = [
  { id: 'autotask',     label: 'Autotask',     flag: 'has_autotask'     },
  { id: 'freshservice', label: 'Freshservice', flag: 'has_freshservice' },
  // { id: 'servicenow', label: 'ServiceNow', flag: 'has_servicenow' },
];

// DEPARTMENT_VIEWS intentionally removed — not yet implemented.
// Design plan: map tickets to departments via a lookup table before adding this back.
// See: ticket_department_map (source, queue_id/category) → 'client_services' | 'internal_it'

const Overview = ({ clientId: clientIdProp, selectedClient: selectedClientProp }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const { user } = useAuth();
  const navigate = useNavigate();

  const clientId = clientIdProp ?? user?.client?.client_id ?? null;
  const selectedClient = selectedClientProp ?? (user?.client ? user.client : null);

  const activeSources = AVAILABLE_SOURCES.filter(
    s => selectedClient?.[s.flag] ?? (s.id === 'autotask')
  );
  const isMultiSource  = activeSources.length > 1;
  const toggleOptions  = isMultiSource
    ? [{ id: 'all', label: 'All Sources' }, ...activeSources]
    : [];

  const [platformFilter, setPlatformFilter] = useState('all');
  const [fsData, setFsData]                     = useState(null);
  const [fsLoading, setFsLoading]               = useState(false);
  const [ovData, setOvData]                     = useState(null);
  const [ovLoading, setOvLoading]               = useState(false);

  const hasSource   = (id) => activeSources.some(s => s.id === id);
  const showOneView = isMultiSource && platformFilter === 'all';
  const showSource  = (id) => !showOneView && hasSource(id) && (platformFilter === 'all' || platformFilter === id);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const ticketsSectionRef = useRef(null);
  const [priorityCollapseCount, setPriorityCollapseCount] = useState(0);

  const handleOpenTicketsClick = () => {
    setPriorityCollapseCount((c) => c + 1);
    setTimeout(() => {
      const el = ticketsSectionRef.current;
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 150);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [loading, setLoading]                     = useState(false);
  const [stats, setStats]                         = useState(null);
  const [summary, setSummary]                     = useState({ newTickets: 0, oldTickets: 0 });
  const [emergencyTickets, setEmergencyTickets]   = useState([]);
  const [kpiTooltip, setKpiTooltip]               = useState(null);
  const [sevenDayTickets, setSevenDayTickets]     = useState([]);
  const [sevenDayOpen, setSevenDayOpen]           = useState(false);
  const [renderedClientId, setRenderedClientId]   = useState(clientId);
  const [openTicketsFilter, setOpenTicketsFilter] = useState("90days");
  const [securedDevicesFilter, setSecuredDevicesFilter] = useState("secured_devices");
  const [clientName, setClientName]                     = useState("");

  // ── INSIGHTS STATE ────────────────────────────────────────────────────────
  const [insightsOpen, setInsightsOpen]         = useState(false);
  const [insightsData, setInsightsData]         = useState(null);
  const [insightsLoading, setInsightsLoading]   = useState(false);
  const [insightsError, setInsightsError]       = useState(false);
  const insightsCachedClientRef                 = React.useRef(null);

  // ── 24HR STATE ──────────────────────────────────────────────────────────────
  const [tickets24hr, setTickets24hr]           = useState([]);
  const [tickets24hrCount, setTickets24hrCount] = useState(0);
  const [show24hrModal, setShow24hrModal]       = useState(false);
  const [loading24hr, setLoading24hr]           = useState(false);
  const [refreshing24hr, setRefreshing24hr]     = useState(false);
  const [lastUpdated, setLastUpdated]           = useState(null);
  const [, setTick]                             = useState(0);

  // ── 24HR FILTER + SORT STATE ────────────────────────────────────────────────
  const [filters24hr, setFilters24hr] = useState({
    num: "", title: "", queue: "", priority: "", status: "", date: "",
  });
  const [sortCol24hr, setSortCol24hr] = useState({ col: "created", dir: 1 });

  if (clientId !== renderedClientId) {
    setRenderedClientId(clientId);
    setStats(null);
    setLoading(true);
    setInsightsOpen(false);
    setInsightsData(null);
    setInsightsError(false);
    insightsCachedClientRef.current = null;
  }

  const formatLastUpdated = (date) => {
    if (!date) return "";
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 30)   return "just now";
    if (diff < 60)   return `${diff}s ago`;
    if (diff < 120)  return "1 min ago";
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const queues24hr = useMemo(
    () => [...new Set(tickets24hr.map(t => t.queue).filter(Boolean))].sort(),
    [tickets24hr]
  );
  const priorities24hr = useMemo(
    () => [...new Set(tickets24hr.map(t => t.priority).filter(Boolean))]
            .sort((a, b) => (PRIORITY_ORDER[a] ?? 99) - (PRIORITY_ORDER[b] ?? 99)),
    [tickets24hr]
  );
  const statuses24hr = useMemo(
    () => [...new Set(tickets24hr.map(t => t.status).filter(Boolean))].sort(),
    [tickets24hr]
  );

  const filteredTickets24hr = useMemo(() => {
    let data = tickets24hr.filter(t => {
      if (filters24hr.num      && !t.ticket_number?.toLowerCase().includes(filters24hr.num.toLowerCase()))  return false;
      if (filters24hr.title    && !t.title?.toLowerCase().includes(filters24hr.title.toLowerCase()))        return false;
      if (filters24hr.queue    && t.queue    !== filters24hr.queue)    return false;
      if (filters24hr.priority && t.priority !== filters24hr.priority) return false;
      if (filters24hr.status   && t.status   !== filters24hr.status)   return false;
      if (filters24hr.date     && !t.created?.startsWith(filters24hr.date)) return false;
      return true;
    });
    data = [...data].sort((a, b) => {
      const { col, dir } = sortCol24hr;
      if (col === "priority") {
        return dir * ((PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
      }
      const av = a[col] ?? "";
      const bv = b[col] ?? "";
      return dir * (av < bv ? -1 : av > bv ? 1 : 0);
    });
    return data;
  }, [tickets24hr, filters24hr, sortCol24hr]);

  useEffect(() => {
    if (show24hrModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [show24hrModal]);

  const handleSort24hr = useCallback((col) => {
    setSortCol24hr(prev => ({ col, dir: prev.col === col ? prev.dir * -1 : 1 }));
  }, []);

  const clearFilters24hr = useCallback(() => {
    setFilters24hr({ num: "", title: "", queue: "", priority: "", status: "", date: "" });
    setSortCol24hr({ col: "created", dir: 1 });
  }, []);

  const fetch24hrCount = useCallback(async () => {
    if (!clientId) return;
    try {
      const count = await fetch24hrTicketCount(clientId);
      setTickets24hrCount(count);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching 24hr count:", err);
    }
  }, [clientId]);

  const handleRefresh = useCallback(async (e) => {
    e.stopPropagation();
    if (refreshing24hr) return;
    setRefreshing24hr(true);
    try {
      const { tickets, count } = await fetch24hrTickets(clientId, true);
      setTickets24hrCount(count);
      setTickets24hr(tickets);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error refreshing 24hr tickets:", err);
    } finally {
      setRefreshing24hr(false);
    }
  }, [clientId, refreshing24hr]);

  const fetchAndShow24hrTickets = useCallback(async () => {
    if (refreshing24hr) return;
    setShow24hrModal(true);
    setLoading24hr(true);
    clearFilters24hr();
    try {
      const { tickets, count } = await fetch24hrTickets(clientId);
      setTickets24hr(tickets);
      setTickets24hrCount(count);
    } catch (err) {
      console.error("Error fetching 24hr tickets:", err);
      setTickets24hr([]);
    } finally {
      setLoading24hr(false);
    }
  }, [clientId, refreshing24hr, clearFilters24hr]);

  useEffect(() => {
    if (!clientId) { setClientName(""); return; }
    if (selectedClient?.client_name) { setClientName(selectedClient.client_name); return; }
    const cacheKey   = `client_name_${clientId}`;
    const cachedName = getCachedData(cacheKey);
    if (cachedName) { setClientName(cachedName); return; }
    (async () => {
      try {
        const list  = await fetchClients();
        const found = list.find(c => c.client_id === parseInt(clientId));
        const name  = found?.client_name || "";
        setClientName(name);
        if (name) setCachedData(cacheKey, name);
      } catch { setClientName(""); }
    })();
  }, [clientId, selectedClient, getCachedData, setCachedData]);

  useEffect(() => {
    setOpenTicketsFilter("90days");
    setSecuredDevicesFilter("secured_devices");
    setTickets24hrCount(0);
    setTickets24hr([]);
    setLastUpdated(null);
    clearFilters24hr();
  }, [clientId, clearFilters24hr]);

  useEffect(() => { fetch24hrCount(); }, [fetch24hrCount]);

  useEffect(() => {
    if (!clientId) {
      setStats(null);
      setSummary({ newTickets: 0, oldTickets: 0 });
      setLoading(false);
      return;
    }

    const cacheKey    = `overview_stats_${clientId}`;
    const cachedStats = getCachedData(cacheKey, clientId);
    if (cachedStats) {
      setStats(cachedStats);
      setLoading(false);
      const cachedAge = getCachedData("overview_tickets_by_age", clientId);
      if (cachedAge) setSummary(computeAgeSummary(cachedAge.labels, cachedAge.values));
      const cached7days = getCachedData(`tickets_7days_${clientId}`, clientId);
      if (cached7days) setSevenDayTickets(cached7days);
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    setStats(null);

    fetchOverviewCombined(clientId, abortController.signal)
      .then((combinedData) => {
        if (!combinedData) return;

        const oc = combinedData.overview_cards || {};
        const statsRes = {
          open_tickets_count_90days:  oc.open_tickets?.count_90days        || 0,
          open_tickets_count_total:   oc.open_tickets?.count_total         || 0,
          open_tickets_count:         oc.open_tickets?.count_90days        || 0,
          high_priority_open_count:   oc.open_tickets?.high_priority_count || 0,
          open_tickets_client_created: oc.open_tickets?.client_created      ?? null,
          open_tickets_alerts:         oc.open_tickets?.alerts              ?? null,
          new_tickets_7days:          oc.new_tickets_7days?.count          ?? 0,
          new_tickets_7days_client_created: oc.new_tickets_7days?.client_created ?? null,
          new_tickets_7days_alerts:         oc.new_tickets_7days?.alerts         ?? null,
          new_tickets_7days_internal:       oc.new_tickets_7days?.internal       ?? null,
          old_tickets_30plus_days:    oc.old_tickets_30plus_days?.count    ?? 0,
          secured_devices_percentage: oc.secured_devices?.percentage       || 0,
          total_devices_count:        oc.secured_devices?.total_devices_count ?? 0,
          has_soc_contract:           oc.secured_devices?.has_soc_contract  ?? false,
          month_open_tickets:         oc.secured_devices?.month_open_tickets || 0,
          year_open_tickets:          oc.secured_devices?.year_open_tickets  || 0,
          total_assets_count:         oc.total_assets?.count               || 0,
          emergency_tickets_count:    oc.emergency_tickets?.count          || 0,
          avg_resolution_time_hours:  oc.avg_resolution_time?.hours        || 0,
        };

        setCachedData(cacheKey, statsRes, clientId);
        setStats(statsRes);

        if (combinedData.priority_distribution)
          setCachedData("ticket_priority_distribution", combinedData.priority_distribution, clientId);
        if (combinedData.recent_tickets)
          setCachedData(`recent_open_tickets_100_${clientId}`, combinedData.recent_tickets, clientId);

        if (combinedData.tickets_by_age) {
          const ageColors = ["#36b9cc", "#4e73df", "#6f42c1", "#f59e0b", "#10b981", "#6b7280"];
          const ageDataWithColors = {
            ...combinedData.tickets_by_age,
            colors: combinedData.tickets_by_age.colors || ageColors,
          };
          setCachedData("overview_tickets_by_age", ageDataWithColors, clientId);
          const today   = new Date();
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          setCachedData(`tickets_by_age_${fmt(weekAgo)}_${fmt(today)}`, ageDataWithColors, clientId);
          setSummary(computeAgeSummary(combinedData.tickets_by_age.labels, combinedData.tickets_by_age.values));
        }

        if (combinedData.tickets_by_status_priority) {
          const now     = new Date();
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          setCachedData(`tickets_by_status_${fmt(weekAgo)}_${fmt(now)}`, combinedData.tickets_by_status_priority, clientId);
        }

        if (combinedData.queue_quarterly) {
          const end   = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 90);
          start.setHours(0,0,0,0);
          end.setHours(0,0,0,0);
          setCachedData(`tickets_by_queue_${start.getTime()}_${end.getTime()}_all`, combinedData.queue_quarterly, clientId);
        }

        if (combinedData.available_statuses)
          setCachedData(`queue_quarterly_statuses_${clientId}`, combinedData.available_statuses, clientId);

        if (combinedData.tickets_7days) {
          setSevenDayTickets(combinedData.tickets_7days);
          setCachedData(`tickets_7days_${clientId}`, combinedData.tickets_7days, clientId);
        }
        if (combinedData.team_details)
          setCachedData("client_team_details", combinedData.team_details, clientId);

        setLoading(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
        console.error("Failed to fetch overview data:", error);
        setLoading(false);
        const defaultStats = {
          open_tickets_count_90days: 0, open_tickets_count_total: 0, open_tickets_count: 0,
          new_tickets_7days: 0, old_tickets_30plus_days: 0, high_priority_open_count: 0,
          secured_devices_percentage: 0, total_assets_count: 0, emergency_tickets_count: 0,
          avg_resolution_time_hours: 0, month_open_tickets: 0, year_open_tickets: 0,
        };
        setStats(defaultStats);
        setCachedData(cacheKey, defaultStats, clientId);
        setSummary({ newTickets: 0, oldTickets: 0 });
      });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // ── FRESHSERVICE DATA FETCH ──────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId || !hasSource('freshservice')) return;
    setFsLoading(true);
    fetchFreshserviceOverview(clientId)
      .then((data) => { setFsData(data); })
      .catch(() => { setFsData(null); })
      .finally(() => { setFsLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, platformFilter]);

  // ── ONEVIEW DATA FETCH ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId || !isMultiSource) return;
    setOvLoading(true);
    fetchOneView(clientId)
      .then((data) => { setOvData(data ? { ...data, tickets: data.tickets || [] } : null); })
      .catch(() => { setOvData(null); })
      .finally(() => { setOvLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // ── NO CLIENT ───────────────────────────────────────────────────────────────
  if (!clientId) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card style={{ borderRadius: 20, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
                  style={{ width: 120, height: 120, borderRadius: "50%", background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114,105,239,0.1) 100%)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24, border: "3px solid var(--color-primary-light)" }}>
                  <FaSearch size={48} color="var(--color-primary)" />
                </motion.div>
                <motion.h3 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="fw-bold mb-3" style={{ fontSize: 28, color: "var(--color-text)", marginBottom: 16 }}>
                  Select a Client
                </motion.h3>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} style={{ fontSize: 16, color: "var(--color-text-light)", maxWidth: 500, lineHeight: 1.6, marginBottom: 32 }}>
                  Please select a client from the search bar above to view their overview data, including tickets, assets, and analytics.
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                  {[{ icon: FaTicketAlt, label: "Open Tickets" }, { icon: FaShieldAlt, label: "Security Metrics" }, { icon: FaChartLine, label: "Analytics" }].map(({ icon: Icon, label }) => (
                    <div key={label} style={{ padding: "12px 20px", borderRadius: 12, background: "var(--color-primary-light)", color: "var(--color-primary)", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon size={16} /><span>{label}</span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  const hasNoData = stats &&
    stats.open_tickets_count === 0 && stats.high_priority_open_count === 0 &&
    stats.secured_devices_percentage === 0 && stats.total_assets_count === 0 &&
    stats.emergency_tickets_count === 0 && stats.new_tickets_7days === 0 &&
    stats.old_tickets_30plus_days === 0 && summary.newTickets === 0 && summary.oldTickets === 0;

  if (clientId && stats && hasNoData) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card style={{ borderRadius: 20, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
                  style={{ width: 120, height: 120, borderRadius: "50%", background: "linear-gradient(135deg, rgba(108,117,125,0.1) 0%, rgba(108,117,125,0.05) 100%)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24, border: "3px solid rgba(108,117,125,0.15)" }}>
                  <FaChartLine size={48} color="var(--color-text-light)" />
                </motion.div>
                <motion.h3 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="fw-bold mb-3" style={{ fontSize: 28, color: "var(--color-text)" }}>
                  No Overview Data Available
                </motion.h3>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} style={{ fontSize: 16, color: "var(--color-text-light)", maxWidth: 500, lineHeight: 1.6, marginBottom: 32 }}>
                  There is no overview data available for the selected client.
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={{ padding: "16px 24px", borderRadius: 12, background: "rgba(108,117,125,0.08)", border: "1px solid rgba(108,117,125,0.15)", fontSize: 14, color: "var(--color-text-light)", maxWidth: 400 }}>
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaExclamationTriangle size={16} />
                    <span>Data will appear here once the client has activity or records</span>
                  </div>
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (loading || !stats) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card style={{ borderRadius: 20, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }}
                  style={{ width: 120, height: 120, borderRadius: "50%", background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114,105,239,0.1) 100%)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24, border: "3px solid var(--color-primary-light)" }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                    <FaSpinner size={48} color="var(--color-primary)" />
                  </motion.div>
                </motion.div>
                <motion.h3 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="fw-bold mb-3" style={{ fontSize: 28, color: "var(--color-text)" }}>
                  Preparing Overview Data
                </motion.h3>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} style={{ fontSize: 16, color: "var(--color-text-light)", maxWidth: 500, lineHeight: 1.6, marginBottom: 32 }}>
                  Preparing overview data for selected client...
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}>
                  {[0, 0.2, 0.4].map((delay, i) => (
                    <motion.div key={i} animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity, delay }}
                      style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)" }} />
                  ))}
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  const PRIORITY_COLORS = {
    Emergency: "#dc2626", Critical: "#ef4444", High: "#f59e0b",
    Medium: "#3b82f6", Low: "#10b981", Normal: "#6b7280", Unknown: "#9ca3af",
  };

  const STATUS_COLORS = {
    Open: "#6f42c1", "In Progress": "#3b82f6", Waiting: "#f59e0b",
    Resolved: "#10b981", Closed: "#6b7280",
  };

  const MODAL_COLUMNS = [
    { key: "ticket_number", label: "Ticket #",  width: 90  },
    { key: "title",         label: "Title",     width: 220 },
    { key: "queue",         label: "Queue",     width: 130 },
    { key: "priority",      label: "Priority",  width: 100 },
    { key: "status",        label: "Status",    width: 140 },
    { key: "created",       label: "Opened",    width: 140 },
  ];

  // ── MAIN RENDER ──────────────────────────────────────────────────────────────
  return (
    <Container fluid className="py-3">


      {/* TITLE CARD */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-4"
        style={{ marginTop: isMobile ? "0.5rem" : "-1.5rem" }}>
        <Card style={{
          borderRadius: 12,
          background: (() => {
            const isDark = document.body.classList.contains("dark-mode");
            return isDark
              ? "linear-gradient(135deg, rgba(111,66,193,0.2) 0%, rgba(139,92,246,0.15) 100%)"
              : "linear-gradient(135deg, rgba(111,66,193,0.1) 0%, rgba(139,92,246,0.08) 100%)";
          })(),
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-sm)",
          overflow: "hidden",
        }}>
          <Card.Body className="p-3">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>

              {/* Left — title */}
              <div className="d-flex align-items-center gap-3">
                <div style={{ background: "rgba(111,66,193,0.15)", padding: 10, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaTachometerAlt size={20} color="#6f42c1" />
                </div>
                <div>
                  <h4 className="fw-bold mb-0" style={{ fontSize: 18, color: "var(--color-text)" }}>
                    Overview Dashboard For{clientName && <span style={{ color: "#6f42c1", marginLeft: 8 }}>{clientName}</span>}
                  </h4>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    Monitor your tickets and team performance
                  </p>
                </div>
              </div>

              {/* Right — all controls in one row */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

                {/* Data Source dropdown */}
                {isMultiSource && (
                  <div style={{ ...badgeBase, padding: isMobile ? "5px 10px" : "5px 12px", gap: 6 }}>
                    <FaChartBar size={12} color="#6f42c1" />
                    <select
                      value={platformFilter}
                      onChange={e => setPlatformFilter(e.target.value)}
                      style={{ background: 'transparent', border: 'none', color: '#6f42c1', fontSize: 13, fontWeight: 500, cursor: 'pointer', outline: 'none', padding: '0 2px' }}
                    >
                      {toggleOptions.map(({ id, label }) => (
                        <option key={id} value={id}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Open Ticket */}
                <div
                  role="button"
                  onClick={() => setShowCreateTicketModal(true)}
                  title="Create a new support ticket"
                  style={{ ...badgeBase, padding: isMobile ? "6px 10px" : "6px 12px", cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(111,66,193,0.17)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(111,66,193,0.1)"; }}
                >
                  <FaPlus size={isMobile ? 10 : 11} color="#6f42c1" />
                  <FaTicketAlt size={isMobile ? 12 : 13} color="#6f42c1" />
                  <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: "#6f42c1" }}>Open Ticket</span>
                </div>

                {/* 24HR badge */}
                <div
                  style={{ ...badgeBase, padding: isMobile ? "5px 8px 5px 10px" : "5px 8px 5px 12px", cursor: refreshing24hr ? "not-allowed" : "pointer", opacity: refreshing24hr ? 0.75 : 1 }}
                  role="button"
                  onClick={fetchAndShow24hrTickets}
                  title="View tickets opened in the last 24 hours"
                  onMouseEnter={e => { if (!refreshing24hr) e.currentTarget.style.background = "rgba(111,66,193,0.17)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(111,66,193,0.1)"; }}
                >
                  <FaClock size={isMobile ? 12 : 13} color="#6f42c1" />
                  <span style={{ background: "#6f42c1", color: "#fff", fontSize: isMobile ? 11 : 12, fontWeight: 700, padding: "1px 7px", borderRadius: 5, lineHeight: "18px", letterSpacing: "0.2px" }}>
                    {refreshing24hr ? "…" : tickets24hrCount}
                  </span>
                  <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500, color: "#6f42c1" }}>Tickets (24h)</span>
                  {lastUpdated && !refreshing24hr && (
                    <span style={{ fontSize: 10, color: "rgba(111,66,193,0.55)", fontWeight: 400, whiteSpace: "nowrap", marginLeft: 2 }}>
                      · {formatLastUpdated(lastUpdated)}
                    </span>
                  )}
                  <span style={{ width: 1, height: 14, background: "rgba(111,66,193,0.25)", flexShrink: 0, marginLeft: 2 }} />
                  <motion.div
                    onClick={handleRefresh}
                    title="Refresh"
                    animate={refreshing24hr ? { rotate: 360 } : { rotate: 0 }}
                    transition={refreshing24hr ? { duration: 0.75, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 5, cursor: refreshing24hr ? "not-allowed" : "pointer", flexShrink: 0 }}
                    onMouseEnter={e => { if (!refreshing24hr) { e.stopPropagation(); e.currentTarget.style.background = "rgba(111,66,193,0.15)"; }}}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <FaSyncAlt size={10} color="#6f42c1" style={{ opacity: 0.75 }} />
                  </motion.div>
                </div>

                {/* Insights */}
                {clientId && (
                  <div
                    role="button"
                    onClick={() => {
                      setInsightsOpen(true);
                      if (insightsCachedClientRef.current !== clientId) {
                        setInsightsLoading(true);
                        setInsightsError(false);
                        getClientInsights(clientId)
                          .then(data => {
                            if (data && (data.same_title !== undefined || data.same_queue !== undefined)) {
                              setInsightsData(data);
                              insightsCachedClientRef.current = clientId;
                            } else {
                              setInsightsError(true);
                            }
                          })
                          .catch(() => setInsightsError(true))
                          .finally(() => setInsightsLoading(false));
                      }
                    }}
                    title="View repeated ticket patterns for this client"
                    style={{
                      background: "linear-gradient(135deg,rgba(245,158,11,0.12),rgba(245,158,11,0.06))",
                      border: "1px solid rgba(245,158,11,0.4)",
                      borderRadius: 10, display: "flex", alignItems: "center", gap: 7,
                      padding: isMobile ? "6px 10px" : "6px 14px",
                      cursor: "pointer", userSelect: "none", transition: "background 0.15s, border-color 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "linear-gradient(135deg,rgba(245,158,11,0.2),rgba(245,158,11,0.12))"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "linear-gradient(135deg,rgba(245,158,11,0.12),rgba(245,158,11,0.06))"; }}
                  >
                    <FaLightbulb size={isMobile ? 12 : 13} color="#b45309" />
                    <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "#92610a" }}>Insights</span>
                  </div>
                )}

                {/* Helpdesk */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(ROUTES.CLIENT_HELPDESK)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(ROUTES.CLIENT_HELPDESK);
                    }
                  }}
                  aria-label="Open Helpdesk analytics"
                  title="View Freshservice helpdesk analytics"
                  style={{
                    background: "rgba(111,66,193,0.1)",
                    border: "1px solid rgba(111,66,193,0.25)",
                    borderRadius: 8, display: "flex", alignItems: "center", gap: 7,
                    padding: isMobile ? "6px 10px" : "6px 14px",
                    cursor: "pointer", userSelect: "none", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(111,66,193,0.18)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(111,66,193,0.1)"; }}
                >
                  <FaHeadset size={isMobile ? 12 : 13} color="#6f42c1" />
                  <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: "#6f42c1" }}>Helpdesk</span>
                </div>

              </div>{/* end right controls row */}
            </div>{/* end single-row wrapper */}
          </Card.Body>
        </Card>
      </motion.div>

      {/* ── ONEVIEW (combined Autotask + Freshservice) ───────────────────────── */}
      {showOneView && (
        <div className="mt-2">
          {ovLoading && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <FaSpinner size={24} style={{ color: 'var(--color-primary)', animation: 'spin 1s linear infinite' }} />
            </div>
          )}
          {!ovLoading && !ovData && (
            <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 32, textAlign: 'center', color: 'var(--color-text-light)' }}>
              <FaTicketAlt size={28} style={{ marginBottom: 10, opacity: 0.4 }} />
              <p style={{ fontSize: 14, marginBottom: 0 }}>No combined data available.</p>
            </div>
          )}
          {!ovLoading && ovData && (
            <>
              {/* KPI stat cards — combined counts */}
              <InViewFadeUp delay={0.1}>
                <div style={{
                  display: 'flex', alignItems: 'stretch',
                  background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-sm)',
                  overflow: 'hidden', marginBottom: 20,
                }}>
                  {[
                    { label: 'Open Tickets',            value: (stats?.open_tickets_count_total || 0) + (ovData.summary.freshservice_active || 0),                                              color: '#6f42c1', iconColor: 'rgba(111,66,193,0.4)',  Icon: LayoutList   },
                    { label: 'Avg. Resolution Time',    value: `${stats?.avg_resolution_time_hours || 0}h`,                                                                                     color: '#14b8a6', iconColor: 'rgba(20,184,166,0.4)',  Icon: Timer        },
                    { label: 'Emergency Tickets',       value: stats?.emergency_tickets_count || 0,                                                                                             color: '#ef4444', iconColor: 'rgba(239,68,68,0.4)',   Icon: AlertCircle  },
                    { label: 'Opened Tickets (7 Days)', value: (stats?.new_tickets_7days || 0) + (ovData.summary.freshservice_new_7days || 0),                                                  color: '#64748b', iconColor: 'rgba(100,116,139,0.4)', Icon: CalendarDays },
                    { label: 'Old Tickets (30+ Days)',  value: ((stats?.old_tickets_30plus_days ?? summary.oldTickets) || 0) + (ovData.summary.freshservice_old_30plus || 0), color: '#f59e0b', iconColor: 'rgba(245,158,11,0.4)',  Icon: History      },
                  ].map(({ label, value, color, iconColor, Icon }, i, arr) => (
                    <React.Fragment key={label}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '28px 24px', gap: 8 }}>
                        <div style={{ fontSize: 40, fontWeight: 800, color, lineHeight: 1, letterSpacing: '-1px' }}>{value}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Icon size={16} strokeWidth={1.5} color={iconColor} />
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.4 }}>{label}</span>
                        </div>
                      </div>
                      {i < arr.length - 1 && <div style={{ width: 1, background: 'var(--color-border)', flexShrink: 0 }} />}
                    </React.Fragment>
                  ))}
                </div>
              </InViewFadeUp>

              {/* Source badge strip */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sources:</span>
                {[
                  { label: 'Autotask',     count: ovData.summary.autotask_total     || 0, color: '#6f42c1', bg: 'rgba(111,66,193,0.1)' },
                  { label: 'Freshservice', count: ovData.summary.freshservice_total || 0, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)'  },
                ].map(({ label, count, color, bg }) => (
                  <span key={label} style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: bg, color, border: `1px solid ${color}30` }}>
                    {label}: {count}
                  </span>
                ))}
              </div>

              {/* Combined status summary row */}
              <div style={{ display: 'flex', alignItems: 'stretch', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                {[
                  { label: 'Total Tickets', value: ovData.summary.total        || 0, color: '#6f42c1' },
                  { label: 'Open',          value: ovData.summary.open         || 0, color: '#6f42c1' },
                  { label: 'In Progress',   value: ovData.summary.in_progress  || 0, color: '#3b82f6' },
                  { label: 'Waiting',       value: ovData.summary.waiting      || 0, color: '#f59e0b' },
                  { label: 'Resolved',      value: ovData.summary.resolved     || 0, color: '#10b981' },
                  { label: 'Closed',        value: ovData.summary.closed       || 0, color: '#6b7280' },
                ].map(({ label, value, color }, i, arr) => (
                  <React.Fragment key={label}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 12px', gap: 6 }}>
                      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{label}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ width: 1, background: 'var(--color-border)', flexShrink: 0 }} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Merged ticket table */}
              {ovData.tickets.length > 0 && (
                <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border)', fontSize: 13, fontWeight: 600, color: 'var(--color-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>All Tickets</span>
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-light)' }}>Showing {ovData.tickets.length} most recent</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }}>
                          {['Source', 'ID', 'Title', 'Priority', 'Status', 'Queue / Category', 'Assignee', 'Updated'].map(h => (
                            <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--color-text-light)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ovData.tickets.map((t, i) => {
                          const pc  = PRIORITY_COLORS[t.priority_normalized] || '#9ca3af';
                          const sc  = STATUS_COLORS[t.status_normalized]     || '#6b7280';
                          const isAT = t.source === 'autotask';
                          return (
                            <tr key={`${t.source}-${t.id}-${i}`}
                              style={{ borderBottom: '1px solid var(--color-border)', background: i % 2 === 0 ? 'transparent' : 'var(--color-bg)', transition: 'background 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(111,66,193,0.04)'}
                              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'var(--color-bg)'}
                            >
                              <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: isAT ? 'rgba(111,66,193,0.12)' : 'rgba(59,130,246,0.12)', color: isAT ? '#6f42c1' : '#3b82f6', border: `1px solid ${isAT ? 'rgba(111,66,193,0.25)' : 'rgba(59,130,246,0.25)'}` }}>
                                  {isAT ? 'AT' : 'FS'}
                                </span>
                              </td>
                              <td style={{ padding: '8px 14px', color: 'var(--color-text-light)', whiteSpace: 'nowrap', fontSize: 12 }}>{t.id}</td>
                              <td style={{ padding: '8px 14px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-text)' }} title={t.title}>{t.title || '—'}</td>
                              <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: `${pc}18`, color: pc, border: `1px solid ${pc}30` }}>{t.priority_normalized || '—'}</span>
                              </td>
                              <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>
                                <span style={{ fontSize: 11, fontWeight: 500, color: sc }}>{t.status_normalized || '—'}</span>
                              </td>
                              <td style={{ padding: '8px 14px', color: 'var(--color-text-light)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.queue || '—'}</td>
                              <td style={{ padding: '8px 14px', color: 'var(--color-text-light)', whiteSpace: 'nowrap' }}>{t.assignee || t.requester || '—'}</td>
                              <td style={{ padding: '8px 14px', color: 'var(--color-text-light)', whiteSpace: 'nowrap', fontSize: 12 }}>
                                {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── AUTOTASK SECTIONS (Client Services) ─────────────────────────────── */}
      {showSource('autotask') && <>

      {/* FIRST SECTION */}
      <Row className="g-4 align-items-stretch">
        <Col lg={12}>
          <InViewFadeUp delay={0.5}>
            <div style={{
              display: "flex", alignItems: "stretch",
              background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-card)", boxShadow: "var(--shadow-sm)", overflow: "hidden",
            }}>
              {[
                { label: "Open Tickets",            value: stats?.open_tickets_count_total || 0,                 color: "#6f42c1", iconColor: "rgba(111,66,193,0.4)",  Icon: LayoutList,   clickable: true },
                { label: "Avg. Resolution Time",    value: `${stats?.avg_resolution_time_hours || 0}h`,          color: "#14b8a6", iconColor: "rgba(20,184,166,0.4)",  Icon: Timer,        clickable: false },
                { label: "Emergency Tickets",       value: stats?.emergency_tickets_count || 0,                  color: "#ef4444", iconColor: "rgba(239,68,68,0.4)",   Icon: AlertCircle,  clickable: false },
                { label: "Opened Tickets (7 Days)", value: stats?.new_tickets_7days || 0,                        color: "#64748b", iconColor: "rgba(100,116,139,0.4)", Icon: CalendarDays, clickable: false, isSevenDay: true },
                { label: "Old Tickets (30+ Days)",  value: stats?.old_tickets_30plus_days ?? summary.oldTickets, color: "#f59e0b", iconColor: "rgba(245,158,11,0.4)",  Icon: History,      clickable: false },
              ].map(({ label, value, color, iconColor, Icon, clickable, isSevenDay }, i, arr) => {
                const isEmergency   = label === "Emergency Tickets";
                const emergencyOn   = isEmergency && Number(value) > 0;
                const handleClick   = isEmergency
                  ? () => window.dispatchEvent(new CustomEvent('open-notifications'))
                  : isSevenDay
                    ? () => setSevenDayOpen(true)
                    : (clickable ? handleOpenTicketsClick : undefined);
                const handleEnter   = isEmergency
                  ? (e) => {
                      e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                      if (emergencyTickets.length > 0) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setKpiTooltip({ tickets: emergencyTickets, x: rect.left, y: rect.bottom });
                      }
                    }
                  : (isSevenDay || clickable) ? (e) => { e.currentTarget.style.background = "rgba(100,116,139,0.08)"; } : undefined;
                const handleLeave   = (isEmergency || isSevenDay || clickable)
                  ? (e) => { e.currentTarget.style.background = ""; setKpiTooltip(null); }
                  : undefined;
                return (
                  <React.Fragment key={label}>
                    <div
                      onClick={handleClick}
                      onMouseEnter={handleEnter}
                      onMouseLeave={handleLeave}
                      style={{
                        flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        padding: "28px 24px", gap: 8,
                        cursor: (isEmergency || isSevenDay || clickable) ? "pointer" : "default",
                        transition: "background 0.18s",
                        background: emergencyOn ? "rgba(239,68,68,0.07)" : "",
                      }}
                    >
                      <div style={{
                        fontSize: 40, fontWeight: 800, lineHeight: 1, letterSpacing: "-1px",
                        color: emergencyOn ? "#dc2626" : color,
                      }}>{value}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Icon size={16} strokeWidth={1.5} color={iconColor} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "1px", lineHeight: 1.4 }}>{label}</span>
                      </div>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ width: 1, background: "var(--color-border)", flexShrink: 0 }} />
                    )}
                  </React.Fragment>
                );
              })}
              {kpiTooltip && (
                <div style={{
                  position: "fixed",
                  top: kpiTooltip.y + 8,
                  left: kpiTooltip.x,
                  transform: "translateX(-50%)",
                  zIndex: 99999,
                  background: "var(--color-bg-card)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  padding: "12px 16px",
                  minWidth: 220,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
                  pointerEvents: "none",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-light)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8 }}>
                    Open Emergency Tickets
                  </div>
                  {kpiTooltip.tickets.map((t, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "var(--color-text)", padding: "4px 0", borderBottom: idx < kpiTooltip.tickets.length - 1 ? "1px solid var(--color-border)" : "none", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 600, color: "#dc2626" }}>#{t.ticket_number || t.ticket_id}</span>
                      {t.title ? `: ${t.title}` : ""}
                      {t.queue ? <span style={{ color: "var(--color-text-light)", fontStyle: "italic" }}> — {t.queue}</span> : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </InViewFadeUp>
        </Col>

        <Col lg={6} className="mb-lg-0 mb-3">
          <InViewFadeUp delay={0.5}>
            <TicketQueueAreaChart clientId={clientId} onHelpdeskBarClick={() => navigate(ROUTES.CLIENT_HELPDESK)} />
          </InViewFadeUp>
        </Col>
        <Col lg={6}>
          <InViewFadeUp delay={0.55}><CreatedClosedChart clientId={clientId} /></InViewFadeUp>
        </Col>

        <Col lg={4} style={{ display: "flex", flexDirection: "column" }}>
          <InViewFadeUp delay={0.65} style={{ flex: 1, display: "flex", flexDirection: "column" }}><PriorityPieChart fetchFunc={fetchTicketPriorityDistribution} clientId={clientId} forceCollapsed={priorityCollapseCount} /></InViewFadeUp>
        </Col>
        <Col lg={8} ref={ticketsSectionRef} style={{ display: "flex", flexDirection: "column" }}>
          <InViewFadeUp delay={0.15} style={{ flex: 1, display: "flex", flexDirection: "column" }}><RecentOpenTicketsTable clientId={clientId} clientName={clientName} /></InViewFadeUp>
        </Col>
      </Row>

      {/* SECOND SECTION */}
      <Row className="mt-4 align-items-start">
        <Col lg={6} className="mb-lg-0 mb-3">
          <InViewFadeUp delay={0.8}><TicketAgeChart clientId={clientId} /></InViewFadeUp>
        </Col>
        <Col lg={6}>
          <InViewFadeUp delay={0.9}><TicketStatusChart clientId={clientId} /></InViewFadeUp>
        </Col>
      </Row>

      {/* THIRD SECTION */}
      <Row className="mt-3">
        <Col lg={12}>
          <InViewFadeUp delay={0.15}><ResourceTable clientId={clientId} /></InViewFadeUp>
        </Col>
      </Row>

      {/* ── 24HR MODAL ─────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {show24hrModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => { setShow24hrModal(false); clearFilters24hr(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25 }}
              onClick={e => e.stopPropagation()}
              style={{ background: "var(--color-bg-card)", borderRadius: 16, width: "100%", maxWidth: 980, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.3)", border: "1px solid var(--color-border)", overflow: "hidden" }}
            >
              {/* Modal Header */}
              <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--color-border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(135deg, rgba(111,66,193,0.08) 0%, rgba(111,66,193,0.04) 100%)", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ background: "rgba(111,66,193,0.15)", padding: "8px 10px", borderRadius: 10 }}>
                    <FaClock size={18} color="#6f42c1" />
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: "var(--color-text)", fontSize: 16 }}>
                      Tickets Opened in Last 24 Hours
                    </h5>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-light)" }}>
                      {loading24hr
                        ? "Loading..."
                        : `${filteredTickets24hr.length}${filteredTickets24hr.length !== tickets24hr.length ? ` of ${tickets24hr.length}` : ""} ticket${tickets24hr.length !== 1 ? "s" : ""} found`
                      }
                      {lastUpdated && !loading24hr && (
                        <span style={{ marginLeft: 8, opacity: 0.6 }}>· {formatLastUpdated(lastUpdated)}</span>
                      )}
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={handleRefresh} disabled={refreshing24hr} title="Refresh"
                    style={{ background: "rgba(111,66,193,0.1)", border: "none", width: 32, height: 32, borderRadius: 8, cursor: refreshing24hr ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <motion.div animate={refreshing24hr ? { rotate: 360 } : { rotate: 0 }} transition={refreshing24hr ? { duration: 0.75, repeat: Infinity, ease: "linear" } : {}}>
                      <FaSyncAlt size={13} color="#6f42c1" />
                    </motion.div>
                  </button>
                  <button onClick={() => { setShow24hrModal(false); clearFilters24hr(); }}
                    style={{ background: "rgba(111,66,193,0.1)", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: "#6f42c1", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    ✕
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ overflowY: "auto", overflowX: "auto", flex: 1, overscrollBehavior: "contain" }}>
                {loading24hr ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-text-light)" }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} style={{ display: "inline-block", marginBottom: 16 }}>
                      <FaSpinner size={28} color="#6f42c1" />
                    </motion.div>
                    <p style={{ margin: 0, fontSize: 14 }}>Fetching tickets...</p>
                  </div>
                ) : tickets24hr.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "var(--color-text-light)" }}>
                    <FaTicketAlt size={36} style={{ marginBottom: 16, opacity: 0.35 }} />
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>No tickets opened in the last 24 hours</p>
                    <p style={{ margin: "6px 0 0", fontSize: 13 }}>Check back later or select a different client</p>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                      <tr>
                        {MODAL_COLUMNS.map(({ key, label, width }) => {
                          const isActive   = sortCol24hr.col === key;
                          const isFiltered = (
                            (key === "ticket_number" && filters24hr.num)      ||
                            (key === "title"         && filters24hr.title)    ||
                            (key === "queue"         && filters24hr.queue)    ||
                            (key === "priority"      && filters24hr.priority) ||
                            (key === "status"        && filters24hr.status)   ||
                            (key === "created"       && filters24hr.date)
                          );
                          return (
                            <th key={key} onClick={() => handleSort24hr(key)}
                              style={{ padding: "10px 12px", textAlign: "left", color: isActive ? "#6f42c1" : "var(--color-text-light)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", background: "var(--color-bg-card)", borderBottom: "1px solid var(--color-border)", minWidth: width }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                {label}
                                <span style={{ display: "flex", flexDirection: "column", lineHeight: 1, gap: 0 }}>
                                  <span style={{ fontSize: 7, lineHeight: 1, color: isActive && sortCol24hr.dir === 1  ? "#6f42c1" : "var(--color-text-light)", opacity: isActive && sortCol24hr.dir === 1  ? 1 : 0.35 }}>▲</span>
                                  <span style={{ fontSize: 7, lineHeight: 1, color: isActive && sortCol24hr.dir === -1 ? "#6f42c1" : "var(--color-text-light)", opacity: isActive && sortCol24hr.dir === -1 ? 1 : 0.35 }}>▼</span>
                                </span>
                                {isFiltered && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6f42c1", display: "inline-block", flexShrink: 0 }} />}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                      <tr>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <input type="text" value={filters24hr.num} onChange={e => setFilters24hr(f => ({ ...f, num: e.target.value }))} placeholder="Search…" style={filterInputStyle} />
                        </th>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <input type="text" value={filters24hr.title} onChange={e => setFilters24hr(f => ({ ...f, title: e.target.value }))} placeholder="Search…" style={filterInputStyle} />
                        </th>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <select value={filters24hr.queue} onChange={e => setFilters24hr(f => ({ ...f, queue: e.target.value }))} style={filterInputStyle}>
                            <option value="">All</option>
                            {queues24hr.map(q => <option key={q} value={q}>{q}</option>)}
                          </select>
                        </th>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <select value={filters24hr.priority} onChange={e => setFilters24hr(f => ({ ...f, priority: e.target.value }))} style={filterInputStyle}>
                            <option value="">All</option>
                            {priorities24hr.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </th>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <select value={filters24hr.status} onChange={e => setFilters24hr(f => ({ ...f, status: e.target.value }))} style={filterInputStyle}>
                            <option value="">All</option>
                            {statuses24hr.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </th>
                        <th style={{ padding: "4px 6px", background: "var(--color-bg-card)", borderBottom: "2px solid var(--color-border)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <input type="date" value={filters24hr.date} onChange={e => setFilters24hr(f => ({ ...f, date: e.target.value }))} style={{ ...filterInputStyle, paddingRight: 2, flex: 1, minWidth: 0 }} />
                            {Object.values(filters24hr).some(Boolean) && (
                              <button onClick={clearFilters24hr} title="Clear all filters"
                                style={{ flexShrink: 0, height: 26, padding: "0 8px", fontSize: 11, fontWeight: 500, background: "rgba(111,66,193,0.08)", color: "#6f42c1", border: "1px solid rgba(111,66,193,0.25)", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", lineHeight: "26px" }}>
                                ✕ Clear
                              </button>
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets24hr.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", padding: "48px 0", color: "var(--color-text-light)" }}>
                            <FaSearch size={28} style={{ marginBottom: 12, opacity: 0.3 }} />
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>No tickets match the selected filters</p>
                            <p style={{ margin: "6px 0 0", fontSize: 12 }}>
                              <span onClick={clearFilters24hr} style={{ color: "#6f42c1", cursor: "pointer", textDecoration: "underline" }}>Clear all filters</span>
                              {" "}to see all {tickets24hr.length} tickets
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredTickets24hr.map((t, i) => {
                          const color = PRIORITY_COLORS[t.priority] || "#9ca3af";
                          return (
                            <tr key={i}
                              style={{ borderBottom: "1px solid var(--color-border)", background: i % 2 === 0 ? "transparent" : "rgba(111,66,193,0.02)", transition: "background 0.15s" }}
                              onMouseEnter={e => e.currentTarget.style.background = "rgba(111,66,193,0.05)"}
                              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "rgba(111,66,193,0.02)"}
                            >
                              <td style={{ padding: "10px 12px", fontWeight: 700, color: "#6f42c1", whiteSpace: "nowrap" }}>{t.ticket_number}</td>
                              <td style={{ padding: "10px 12px", color: "var(--color-text)", maxWidth: 220, position: "relative" }}>
                                <TitleTooltip title={t.title} />
                              </td>
                              <td style={{ padding: "10px 12px", color: "var(--color-text-light)", whiteSpace: "nowrap" }}>{t.queue}</td>
                              <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                                <span style={{ background: `${color}20`, color, padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, border: `1px solid ${color}30` }}>{t.priority}</span>
                              </td>
                              <td style={{ padding: "10px 12px", color: "var(--color-text-light)", whiteSpace: "nowrap", fontSize: 12 }}>{t.status}</td>
                              <td style={{ padding: "10px 12px", color: "var(--color-text-light)", whiteSpace: "nowrap", fontSize: 12 }}>{t.created}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Modal Footer */}
              {!loading24hr && tickets24hr.length > 0 && Object.values(filters24hr).some(Boolean) && (
                <div style={{ padding: "8px 24px", borderTop: "1px solid var(--color-border)", background: "rgba(111,66,193,0.02)", flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    Showing <strong style={{ color: "#6f42c1" }}>{filteredTickets24hr.length}</strong> of {tickets24hr.length} ticket{tickets24hr.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── END AUTOTASK SECTIONS ────────────────────────────────────────────── */}
      </>}

      {/* ── FRESHSERVICE SECTION (Internal IT) ───────────────────────────────── */}
      {showSource('freshservice') && (
        <div className="mt-2">
          {fsLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <FaSpinner size={24} className="fa-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          ) : !fsData ? (
            <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 32, textAlign: 'center', color: 'var(--color-text-light)' }}>
              <FaTicketAlt size={28} style={{ marginBottom: 10, opacity: 0.4 }} />
              <p style={{ fontSize: 14, marginBottom: 0 }}>No Internal IT data available.</p>
            </div>
          ) : (
            <>
              {/* Summary stat row */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { label: 'Open',        value: fsData.summary.open,        color: '#6f42c1' },
                  { label: 'In Progress', value: fsData.summary.in_progress, color: '#3b82f6' },
                  { label: 'Waiting',     value: fsData.summary.waiting,     color: '#f59e0b' },
                  { label: 'Resolved',    value: fsData.summary.resolved,    color: '#10b981' },
                  { label: 'Closed',      value: fsData.summary.closed,      color: '#6b7280' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ flex: '1 1 140px', minWidth: 120, background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '16px 20px' }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-light)', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Priority breakdown */}
              {fsData.by_priority.length > 0 && (
                <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 10 }}>Tickets by Priority</div>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {fsData.by_priority.map(({ label, count }) => {
                      const colors = { Critical: '#ef4444', High: '#f59e0b', Medium: '#3b82f6', Low: '#10b981' };
                      return (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: colors[label] || '#6b7280' }} />
                          <span style={{ fontSize: 13, color: 'var(--color-text)' }}>{label}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: colors[label] || '#6b7280' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent tickets table */}
              {fsData.recent_tickets.length > 0 && (
                <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border)', fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
                    Recent Internal IT Tickets
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }}>
                          {['#', 'Subject', 'Category', 'Priority', 'Status', 'Requester', 'Updated'].map(h => (
                            <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-light)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fsData.recent_tickets.map((t, i) => (
                          <tr key={t.id} style={{ borderBottom: '1px solid var(--color-border)', background: i % 2 === 0 ? 'transparent' : 'var(--color-bg)' }}>
                            <td style={{ padding: '8px 14px', color: 'var(--color-text-light)' }}>{t.id}</td>
                            <td style={{ padding: '8px 14px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                            <td style={{ padding: '8px 14px', color: 'var(--color-text-light)' }}>{t.category || '—'}</td>
                            <td style={{ padding: '8px 14px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                background: { Critical: '#fee2e2', High: '#fef3c7', Medium: '#dbeafe', Low: '#d1fae5' }[t.priority_normalized] || '#f3f4f6',
                                color: { Critical: '#dc2626', High: '#d97706', Medium: '#2563eb', Low: '#059669' }[t.priority_normalized] || '#6b7280',
                              }}>{t.priority || '—'}</span>
                            </td>
                            <td style={{ padding: '8px 14px', color: 'var(--color-text-light)' }}>{t.status || '—'}</td>
                            <td style={{ padding: '8px 14px', color: 'var(--color-text-light)' }}>{t.requester || '—'}</td>
                            <td style={{ padding: '8px 14px', color: 'var(--color-text-light)', whiteSpace: 'nowrap' }}>
                              {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Seven Day Ticket Drawer */}
      <SevenDayDrawer open={sevenDayOpen} onClose={() => setSevenDayOpen(false)} tickets={sevenDayTickets} />

      {/* Create Support Ticket Modal */}
      <CreateTicketModal
        open={showCreateTicketModal}
        onClose={() => setShowCreateTicketModal(false)}
        userEmail={user?.email || user?.username || ""}
        clientName={clientName || selectedClient?.client_name || ""}
      />

      {/* Client Insights Drawer */}
      <ClientInsightsDrawer
        open={insightsOpen}
        onClose={() => setInsightsOpen(false)}
        error={insightsError}
        data={insightsData}
        loading={insightsLoading}
        clientName={clientName || selectedClient?.client_name || ""}
      />

      {/* ── AI Chatbot ── */}
      <OverviewChatbot clientId={clientId} />

    </Container>
  );
};

export default Overview;